import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PartnersService } from '~/app/services/partners/partners.service';
import type {
  AllMaintenanceScopeDimension,
  MaintenanceSlaScopeOption,
  MaintenanceSlaScopeOptions,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';

const EMPTY_SCOPE_OPTIONS: MaintenanceSlaScopeOptions = {
  manufacturers: [],
  models: [],
  departments: [],
  communes: [],
  postcodes: [],
  assets: [],
  assetHierarchy: [],
};

export function scopeOptionsForDimension(
  options: MaintenanceSlaScopeOptions,
  dimension: AllMaintenanceScopeDimension,
): MaintenanceSlaScopeOption[] {
  switch (dimension) {
    case 'MANUFACTURER':
      return options.manufacturers;
    case 'MODEL':
      return options.models;
    case 'COMMUNE':
      return options.communes;
    case 'POSTCODE':
      return options.postcodes;
    case 'DEPARTMENT':
      return options.departments;
    case 'ASSET':
      return options.assets;
  }
}

export function useMaintenanceSlaScopeOptions(
  organizationId: string,
  excludeRelationshipId?: string,
) {
  const query = useQuery({
    queryKey: [
      'partners',
      'maintenance-rules',
      'scope-options',
      organizationId,
      excludeRelationshipId ?? '',
    ],
    queryFn: () =>
      PartnersService.listPartnerMaintenanceScopeOptions(
        organizationId,
        excludeRelationshipId,
      ),
    enabled: organizationId.length > 0,
  });

  const options = query.data ?? EMPTY_SCOPE_OPTIONS;

  const labelFor = useCallback(
    (dimension: AllMaintenanceScopeDimension, value: string | null): string =>
      scopeOptionsForDimension(options, dimension).find(
        (option) => option.value === value,
      )?.label ??
      value ??
      '',
    [options],
  );

  return {
    options,
    isLoading: query.isLoading,
    labelFor,
  };
}
